// ShuttleAreaLayer — Wave 14dt
// =============================
//
// Rendert Shuttle-Area-Badges (PL/KA/IS/ME/BN) als runde DOM-Marker auf
// der Karte. Aktiv nur im Shuttle-Mode (MapPage steuert das per Prop).
//
// Badge-Design:
//   - 48px rund, sky-500 Hintergrund, weiße Border
//   - 2-Buchstaben-Code zentral, fett weiß, ~18px
//   - Subtle Box-Shadow
//   - Beim Active-State (ausgewählt): orange Border statt weiß
//
// Tap → ruft props.onSelect(area). Caller (MapPage) zoomt dann auf die
// Runs dieser Area + zeigt die Carousel-Sheet.

import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import type { ShuttleArea } from '@adtu/shared';

interface Props {
  map: maplibregl.Map | null;
  areas: ShuttleArea[];
  onSelect: (area: ShuttleArea) => void;
  /** Code der aktuell aktiven Area — kriegt einen orangenen Akzent-Rahmen. */
  activeCode?: string | null;
}

export function ShuttleAreaLayer({
  map,
  areas,
  onSelect,
  activeCode = null,
}: Props) {
  const markersRef = useRef<maplibregl.Marker[]>([]);

  useEffect(() => {
    if (!map) return;
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];

    for (const area of areas) {
      const isActive = area.code === activeCode;
      const el = buildBadgeElement(area, isActive);
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        onSelect(area);
      });
      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'center',
        opacityWhenCovered: '1',
      })
        .setLngLat([area.lon, area.lat])
        .addTo(map);
      markersRef.current.push(marker);
    }

    return () => {
      for (const m of markersRef.current) m.remove();
      markersRef.current = [];
    };
  }, [map, areas, onSelect, activeCode]);

  return null;
}

function buildBadgeElement(area: ShuttleArea, isActive: boolean): HTMLDivElement {
  const container = document.createElement('div');
  container.className = 'adt-shuttle-area-badge';
  container.title = area.name;
  container.style.cursor = 'pointer';
  container.style.userSelect = 'none';
  container.style.touchAction = 'manipulation';
  container.style.width = '48px';
  container.style.height = '48px';
  container.style.borderRadius = '50%';
  container.style.background = '#0284c7'; // sky-600 — punchy, visible on map
  container.style.border = isActive
    ? '3px solid #f97316' // orange-500 — active accent
    : '3px solid #ffffff';
  container.style.boxShadow = '0 2px 6px rgba(0,0,0,0.35)';
  container.style.display = 'flex';
  container.style.alignItems = 'center';
  container.style.justifyContent = 'center';
  container.style.color = '#ffffff';
  container.style.fontWeight = '700';
  container.style.fontSize = '17px';
  container.style.fontFamily =
    "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
  container.style.letterSpacing = '0.5px';
  container.textContent = area.code;
  return container;
}
